import { create } from "zustand";
import api from "../../utils/api";
import { getError } from "../../utils/getError";

export interface CompanySource {
  id: string;
  active: boolean;
  type: string | null;
  provider_id: string | null;
  imeis: string | null;
  max_parallel: number | null;
  poll_interval_sec: number | null;
  credentials: Record<string, string>;
  credentials_version: string;
  lastDiscoveryAt: { _seconds: number } | null;
  lastPollAt: { _seconds: number } | null;
  createdAt?: { _seconds: number };
  updatedAt?: { _seconds: number };
}

export interface CompanySourceFormData {
  active: boolean;
  type: string;
  provider_id: string;
  imeis: string;
  max_parallel: number | null;
  poll_interval_sec: number | null;
  credentials: Record<string, string>;
  credentials_version: string;
}

interface SourceFilterParams {
  page?: number;
  limit?: number;
  search?: string;
  active?: string;
  type?: string;
}

interface CompanySourcesStore {
  sources: CompanySource[];
  loading: boolean;
  fetchSources: (companyId: string, params?: SourceFilterParams) => Promise<void>;
  createSource: (
    companyId: string,
    data: CompanySourceFormData
  ) => Promise<{ success: boolean; error?: string; data?: CompanySource }>;
  updateSource: (
    companyId: string,
    sourceId: string,
    data: Partial<CompanySourceFormData>
  ) => Promise<{ success: boolean; error?: string }>;
  deleteSource: (
    companyId: string,
    sourceId: string
  ) => Promise<{ success: boolean; error?: string }>;
  clearSources: () => void;
}

// Compañía cuya carga es la "vigente": descarta respuestas obsoletas al
// cambiar de compañía rápidamente.
let activeRequestCompanyId: string | null = null;

export const useCompanySourcesStore = create<CompanySourcesStore>((set, get) => ({
  sources: [],
  loading: false,

  fetchSources: async (companyId: string, params?: SourceFilterParams) => {
    activeRequestCompanyId = companyId;
    set({ loading: true, sources: [] });
    try {
      const { data } = await api.get("/companySourcesList", {
        params: {
          company_id: companyId,
          limit: params?.limit ?? 50,
          page: params?.page ?? 1,
          ...(params?.search ? { search: params.search } : {}),
          ...(params?.active ? { active: params.active } : {}),
          ...(params?.type ? { type: params.type } : {}),
        },
      });
      if (activeRequestCompanyId !== companyId) return;
      set({ sources: data.data || [], loading: false });
    } catch (error) {
      console.error("Error fetching company sources:", error);
      if (activeRequestCompanyId !== companyId) return;
      set({ sources: [], loading: false });
    }
  },

  createSource: async (companyId, data) => {
    try {
      const response = await api.post("/companySource", data, {
        params: { company_id: companyId },
      });
      if (response.data?.data) {
        get().fetchSources(companyId);
        return { success: true, data: response.data.data };
      }
      return { success: false, error: "Error creating source" };
    } catch (error) {
      console.error("Error creating company source:", error);
      return { success: false, error: getError(error) };
    }
  },

  updateSource: async (companyId, sourceId, data) => {
    try {
      const response = await api.put("/companySource", data, {
        params: { company_id: companyId, id: sourceId },
      });
      if (response.data) {
        get().fetchSources(companyId);
        return { success: true };
      }
      return { success: false, error: "Error updating source" };
    } catch (error) {
      console.error("Error updating company source:", error);
      return { success: false, error: getError(error) };
    }
  },

  deleteSource: async (companyId, sourceId) => {
    try {
      await api.delete("/companySource", {
        params: { company_id: companyId, id: sourceId },
      });
      get().fetchSources(companyId);
      return { success: true };
    } catch (error) {
      console.error("Error deleting company source:", error);
      return { success: false, error: getError(error) };
    }
  },

  clearSources: () => {
    activeRequestCompanyId = null;
    set({ sources: [] });
  },
}));
